import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CHILD_TICKET_GROUPS,
  findChildTicketKind,
} from '@cortex/domain-product-innovation-lab-basic-design-tree';
import { childTicketToneStyle } from './ChildTicketKindLabel.js';
import {
  isOverChildTicketPointLimit,
  totalResolvedPoint,
} from '@cortex/domain-product-innovation-lab-basic-design-tree';
import type { ChildTicketChunk } from './child-ticket-chunks.js';

/** 包みを開いたときに、そこから広がって見せるための元の位置と大きさ。 */
export interface ChunkOpenOrigin {
  /** 画面左端からの位置 (px)。 */
  left: number;
  /** 画面上端からの位置 (px)。 */
  top: number;
  /** 包みの幅 (px)。 */
  width: number;
}

/**
 * 1 枚ぶんの包み。中身は見出しではなく**数**で見せる（子票の枚数・合計 pt・群の内訳）。
 * 名前はその場で書き換えられ、取っ手を掴めば並べ替えられる。
 * @graph-connects totalResolvedPoint [calls] そのチャンクの合計 pt
 * @graph-connects isOverChildTicketPointLimit [calls] 上限超過の判定
 * @graph-connects findChildTicketKind [calls] 行の種別から群を引く
 */
function ChunkCard({
  chunk,
  index,
  dragging,
  dropTarget,
  expanded,
  onOpen,
  onRename,
  onRemove,
  onDragStart,
  onDragEnd,
}: {
  chunk: ChildTicketChunk;
  index: number;
  dragging: boolean;
  dropTarget: boolean;
  /** Backlog へ展開済みか。展開済みは消せない。 */
  expanded: boolean;
  onOpen: (origin: ChunkOpenOrigin) => void;
  onRename: (title: string) => void;
  onRemove: () => void;
  onDragStart: (dragImage: HTMLElement | null) => void;
  onDragEnd: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [editing, setEditing] = useState(false);

  return (
    <div
      ref={cardRef}
      data-dragging={dragging}
      data-drop-target={dropTarget}
      className="child-chunk-card group/chunk relative flex flex-col gap-3 rounded-2xl p-4 text-left"
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          aria-label={t('childTicketPlan.chunks.drag', { title: chunk.title })}
          // 掴めるのは取っ手だけ。カード全体を draggable にすると、名前の書き換えや
          // 「開く」の押下がそのまま掴み上げに化ける
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', String(index));
            if (cardRef.current !== null) e.dataTransfer.setDragImage(cardRef.current, 24, 16);
            onDragStart(cardRef.current);
          }}
          onDragEnd={onDragEnd}
          className="mt-0.5 grid h-5 w-5 shrink-0 cursor-grab place-items-center rounded-md text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-secondary)] active:cursor-grabbing"
        >
          <span aria-hidden="true">⠿</span>
        </button>

        {editing ? (
          <input
            type="text"
            defaultValue={chunk.title}
            autoFocus
            aria-label={t('childTicketPlan.chunks.rename', { title: chunk.title })}
            onBlur={(e) => {
              onRename(e.target.value);
              setEditing(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') setEditing(false);
            }}
            className="w-full rounded-lg border border-[var(--color-border-focus)] bg-[var(--color-bg-surface)] px-2 py-1 text-sm font-semibold text-[var(--color-text-primary)] focus:outline-none"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setEditing(true);
            }}
            className="flex-1 cursor-text pr-6 text-left text-sm font-semibold break-all text-[var(--color-text-primary)] hover:underline"
          >
            {chunk.title}
          </button>
        )}
      </div>

      {/* 展開済みのチャンクは消せない。Backlog に母票・中継チケット・子票が既にあり、
          画面から外しても向こうは残るので「消えた」と誤解させないために口ごと出さない */}
      {expanded ? (
        <span
          title={t('childTicketPlan.chunks.expandedHint')}
          className="absolute top-3 right-3 rounded-full bg-[var(--color-child-ticket-chunk)] px-2 py-0.5 text-[10px] font-semibold text-[var(--color-child-ticket-chunk-on)]"
        >
          {t('childTicketPlan.chunks.expanded')}
        </span>
      ) : (
        <button
          type="button"
          aria-label={t('childTicketPlan.chunks.remove', { title: chunk.title })}
          title={t('childTicketPlan.chunks.remove', { title: chunk.title })}
          onClick={onRemove}
          className="child-ticket-action absolute top-3 right-3 grid h-6 w-6 cursor-pointer place-items-center rounded-full text-xs text-[var(--color-text-muted)] opacity-0 transition-opacity group-hover/chunk:opacity-100 hover:text-[var(--color-error)]"
        >
          <span aria-hidden="true">✕</span>
        </button>
      )}

      <button
        type="button"
        onClick={() => {
          const rect = cardRef.current?.getBoundingClientRect();
          onOpen(
            rect === undefined
              ? { left: 0, top: 0, width: 0 }
              : { left: rect.left, top: rect.top, width: rect.width },
          );
        }}
        aria-label={t('childTicketPlan.chunks.open', { title: chunk.title })}
        className="flex cursor-pointer flex-col gap-3 text-left"
      >
        {/* 包みの中身。枚数と pt を数で見せ、群の内訳を色で添える */}
        <div className="flex items-baseline gap-3">
          <span className="rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[11px] tabular-nums text-[var(--color-text-muted)]">
            {chunk.rows.length}
          </span>
          <span className="flex items-baseline gap-1">
            {/* 上限を超えた包みは、開く前に数字の色だけで分かるようにする */}
            <span
              className="font-mono text-base font-semibold tabular-nums"
              style={{
                color: isOverChildTicketPointLimit(totalResolvedPoint(chunk.rows))
                  ? 'var(--color-error)'
                  : 'var(--color-text-primary)',
              }}
            >
              {totalResolvedPoint(chunk.rows)}
            </span>
            <span className="text-[11px] text-[var(--color-text-muted)]">
              {t('childTicketPlan.columns.point')}
            </span>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {CHILD_TICKET_GROUPS.map((group) => {
            const count = chunk.rows.filter(
              (row) => findChildTicketKind(row.kindId)?.group === group.id,
            ).length;
            if (count === 0) return null;
            return (
              <span
                key={group.id}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                style={childTicketToneStyle(group.tone)}
              >
                {t(group.labelKey)}
                <span className="tabular-nums opacity-80">{count}</span>
              </span>
            );
          })}
        </div>
      </button>
    </div>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 切り出したチャンクの一覧。包みを並べて中身の枚数と pt を見せ、名前の書き換え・削除・並べ替え・新しいチャンクの追加ができる。包みを押すとそのチャンクの基本設計シートへ、包みの位置から広がって戻る
 * @graph-connects ChunkCard [calls] 包み 1 枚の表示
 */
export function ChildTicketChunkBoard({
  chunks,
  expandedChunkIds,
  onOpen,
  onRename,
  onRemove,
  onMove,
  onAdd,
}: {
  /** 並べるチャンク。 */
  chunks: readonly ChildTicketChunk[];
  /** Backlog へ展開済みのチャンク id。ここに入っているものは消せない。 */
  expandedChunkIds: ReadonlySet<string>;
  /** 包みを開く（そのチャンクのシート編集へ戻る）。第 2 引数は広がりの起点。 */
  onOpen: (chunkId: string, origin: ChunkOpenOrigin) => void;
  /** 名前を書き換える。 */
  onRename: (chunkId: string, title: string) => void;
  /** 包みを外す。 */
  onRemove: (chunkId: string) => void;
  /** 並べ替える（`from` 番目を `to` 番目へ）。 */
  onMove: (from: number, to: number) => void;
  /** 新しいチャンクを足す。 */
  onAdd: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {chunks.map((chunk, index) => (
        <div
          key={chunk.chunkId}
          className="child-chunk-pop"
          style={{ animationDelay: `${String(index * 60)}ms` }}
          onDragOver={(e) => {
            if (dragIndex === null) return;
            // preventDefault しないと drop が起きない（HTML のドラッグの決まり）
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDropIndex(index);
          }}
          onDrop={(e) => {
            if (dragIndex === null) return;
            e.preventDefault();
            if (dragIndex !== index) onMove(dragIndex, index);
            setDragIndex(null);
            setDropIndex(null);
          }}
        >
          <ChunkCard
            chunk={chunk}
            index={index}
            dragging={dragIndex === index}
            dropTarget={dropIndex === index && dragIndex !== index}
            expanded={expandedChunkIds.has(chunk.chunkId)}
            onOpen={(origin) => {
              onOpen(chunk.chunkId, origin);
            }}
            onRename={(title) => {
              onRename(chunk.chunkId, title);
            }}
            onRemove={() => {
              onRemove(chunk.chunkId);
            }}
            onDragStart={() => {
              setDragIndex(index);
            }}
            onDragEnd={() => {
              setDragIndex(null);
              setDropIndex(null);
            }}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={onAdd}
        className="child-chunk-add grid min-h-[132px] cursor-pointer place-items-center rounded-2xl text-sm font-medium"
      >
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true">＋</span>
          {t('childTicketPlan.chunks.add')}
        </span>
      </button>
    </div>
  );
}
